import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Star, X, CheckCircle } from "@phosphor-icons/react";

const STATUS_STYLE = {
  confirmed: "text-[#6BB9B8] border-[#6BB9B8]/40",
  pending: "text-[#D69A2D] border-[#D69A2D]/40",
  expired: "text-[#003D4C]/45 border-[#003D4C]/15",
  cancelled: "text-[#003D4C]/45 border-[#003D4C]/15",
};
const STATUS_LABEL = {
  confirmed: "Confirmada",
  pending: "Pendente",
  expired: "Expirada",
  cancelled: "Cancelada",
};

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function MyBookings() {
  const [bookings, setBookings] = useState(null);
  const [myReviews, setMyReviews] = useState([]);
  const [modalBooking, setModalBooking] = useState(null);

  const refresh = () => {
    api.get("/bookings/me").then(({ data }) => setBookings(data)).catch(() => setBookings([]));
    api.get("/reviews/me").then(({ data }) => setMyReviews(data)).catch(() => setMyReviews([]));
  };
  useEffect(refresh, []);

  const todayISO = toISO(new Date());
  const reviewedIds = new Set(myReviews.map((r) => r.booking_id));
  const canReview = (b) => b.status === "confirmed" && b.date <= todayISO && !reviewedIds.has(b.booking_id);
  const reviewStatus = (b) => myReviews.find((r) => r.booking_id === b.booking_id)?.status;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14" data-testid="my-bookings-page">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4 fade-in">
        <div>
          <p className="overline">— Histórico</p>
          <h1 className="font-display text-5xl md:text-6xl mt-3 text-[#003D4C]">As minhas reservas</h1>
        </div>
        <Link to="/book" className="btn-hot px-5 py-2.5" data-testid="new-booking-btn">Nova reserva</Link>
      </div>

      {!bookings ? (
        <p className="text-[#003D4C]/55 text-sm">A carregar…</p>
      ) : bookings.length === 0 ? (
        <div className="card text-center p-12" data-testid="bookings-empty">
          <p className="font-display text-3xl text-[#003D4C]">Ainda sem reservas.</p>
          <p className="text-[#003D4C]/60 mt-2">Comece pelo ciclo completo — sauna seguida de banho de gelo.</p>
          <Link to="/book" className="btn-hot inline-block mt-6 px-6 py-3">Reservar agora</Link>
        </div>
      ) : (
        <div className="border hairline bg-white divide-y divide-[#003D4C]/8" data-testid="bookings-list">
          {bookings.map((b) => {
            const rev = reviewStatus(b);
            return (
              <div key={b.booking_id} className="p-5 grid grid-cols-12 items-center gap-4" data-testid={`booking-${b.booking_id}`}>
                <div className="col-span-12 md:col-span-3">
                  <div className="font-display text-2xl text-[#003D4C]">{b.service_label}</div>
                  <div className="text-xs text-[#003D4C]/50 mt-1">Ref. {b.booking_id}</div>
                </div>
                <div className="col-span-6 md:col-span-3 text-sm text-[#003D4C]">
                  <div className="overline">Data</div>
                  <div className="mt-1">{new Date(b.date + "T00:00:00").toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" })}</div>
                </div>
                <div className="col-span-3 md:col-span-2 text-sm text-[#003D4C]">
                  <div className="overline">Hora</div>
                  <div className="mt-1">{String(b.hour).padStart(2, "0")}:00</div>
                </div>
                <div className="col-span-3 md:col-span-1 text-sm">
                  <div className="overline">Total</div>
                  <div className="mt-1 font-display text-xl text-[#003D4C]">€{b.amount}</div>
                </div>
                <div className="col-span-12 md:col-span-3 flex md:justify-end items-center gap-2 flex-wrap">
                  <span className={`inline-block border px-3 py-1.5 text-xs uppercase tracking-wider ${STATUS_STYLE[b.status] || "text-[#003D4C]/55 border-[#003D4C]/15"}`}>
                    {STATUS_LABEL[b.status] || b.status}
                  </span>
                  {canReview(b) && (
                    <button onClick={() => setModalBooking(b)} className="btn-hot px-3 py-1.5 text-xs flex items-center gap-1.5" data-testid={`review-btn-${b.booking_id}`}>
                      <Star size={12} weight="fill" /> Avaliar
                    </button>
                  )}
                  {rev === "pending" && <span className="text-[11px] text-[#D69A2D] uppercase tracking-wider">Em moderação</span>}
                  {rev === "approved" && <span className="text-[11px] text-[#6BB9B8] uppercase tracking-wider inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Publicada</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalBooking && (
        <ReviewModal booking={modalBooking} onClose={() => setModalBooking(null)} onSuccess={() => { setModalBooking(null); refresh(); }} />
      )}
    </div>
  );
}

function ReviewModal({ booking, onClose, onSuccess }) {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await api.post("/reviews", { booking_id: booking.booking_id, rating, comment: comment.trim() });
      onSuccess();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#003D4C]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="review-modal" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border hairline w-full max-w-lg p-8 relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#003D4C]/55 hover:text-[#003D4C]"
          data-testid="review-modal-close" aria-label="Fechar">
          <X size={20} weight="light" />
        </button>

        <p className="overline">— Avaliar sessão</p>
        <h2 className="font-display text-3xl mt-3 text-[#003D4C]">{booking.service_label}</h2>
        <p className="text-sm text-[#003D4C]/65 mt-1">
          {new Date(booking.date + "T00:00:00").toLocaleDateString("pt-PT", { day: "2-digit", month: "long" })} · {String(booking.hour).padStart(2, "0")}:00
        </p>

        <label className="overline block mt-8 mb-3">Pontuação</label>
        <div className="flex gap-1" data-testid="rating-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)} data-testid={`rating-${n}`} className="p-1">
              <Star size={28} weight={(hover || rating) >= n ? "fill" : "regular"} color={(hover || rating) >= n ? "#D69A2D" : "#9D968D"} />
            </button>
          ))}
        </div>

        <label className="overline block mt-6 mb-2">Comentário</label>
        <textarea className="field" rows={4} maxLength={500} required value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder="O que sentiu? O que destacaria?" data-testid="review-comment" />
        <p className="text-[11px] text-[#003D4C]/45 mt-1">{comment.length} / 500 · Fica visível depois de aprovada.</p>

        {err && <p className="text-sm text-[#BE3A34] mt-3" data-testid="review-error">{err}</p>}

        <button type="submit" disabled={submitting || comment.trim().length < 3} className="btn-hot w-full py-3 mt-6" data-testid="review-submit">
          {submitting ? "A enviar…" : "Enviar avaliação"}
        </button>
      </form>
    </div>
  );
}
